import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link } from "react-router";

import { reopenFlag, resolveFlag, setUserActive } from "../../api/admin";
import type { Flag, FlagAction, FlagResolve } from "../../api/admin";
import { errorText } from "../../api/client";
import { Button, buttonClass } from "../../components/Button";
import { Field, FormError } from "../../components/Field";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { formatDateTime } from "../../lib/format";
import { MergeDialog } from "./MergeDialog";
import styles from "./moderation.module.css";
import { useRefreshModeration } from "./queries";

const t = nl.admin.moderation;

type Person = NonNullable<Flag["reporter"]>;

function personText(person: Person): string {
  return `${person.initials} · ${person.email}`;
}

function TextArea({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: string;
  max: number;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <textarea
        id={id}
        className={styles.textarea}
        rows={3}
        maxLength={max}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

function ReportBlock({ flag }: { flag: Flag }) {
  const report = flag.report;
  if (!report) return null;
  return (
    <div className={styles.target}>
      <div className={styles.targetHead}>
        <span className={styles.targetTitle}>{report.company_name}</span>
        <span className={styles.meta}>
          {nl.companies.outcomes[report.outcome]} · {formatDateTime(report.created_at)}
        </span>
      </div>
      {report.reason_note && (
        <div>
          <div className={styles.label}>{t.reasonNote}</div>
          <p className={styles.quote}>{report.reason_note}</p>
        </div>
      )}
      {report.tip_text && (
        <div>
          <div className={styles.label}>{t.tipText}</div>
          <p className={styles.quote}>{report.tip_text}</p>
        </div>
      )}
      {!report.reason_note && !report.tip_text && <p className={styles.meta}>{t.noText}</p>}
      <p className={styles.meta}>
        {t.author}: {report.author ? personText(report.author) : t.noAuthor}
        {report.anonymous && <span className={styles.tag}>{t.anonymous}</span>}
      </p>
      {report.hidden_at && <p className={styles.hiddenNote}>{t.hidden(report.hidden_reason ?? "")}</p>}
      <Link className={buttonClass("ghost")} to={`/bedrijven/${report.company_id}`}>
        {t.openCompany}
      </Link>
    </div>
  );
}

function TargetBlock({ flag }: { flag: Flag }) {
  if (flag.report) return <ReportBlock flag={flag} />;
  if (flag.company) {
    const company = flag.company;
    return (
      <div className={styles.target}>
        <span className={styles.targetTitle}>{company.name}</span>
        <span className={styles.meta}>
          {[company.municipality, t.kbo(company.kbo_number ?? null), company.needs_review ? t.needsReview : null]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <Link className={buttonClass("ghost")} to={`/bedrijven/${company.id}`}>
          {t.openCompany}
        </Link>
      </div>
    );
  }
  if (flag.user) {
    const user = flag.user;
    return (
      <div className={styles.target}>
        <span className={styles.targetTitle}>{user.email}</span>
        <span className={styles.meta}>
          {user.initials} · {t.userStatus[user.status]}
        </span>
      </div>
    );
  }
  return <p className={styles.meta}>{nl.companies.notFound}</p>;
}

/** Edit the free text with a before/after preview; saving overwrites the old text. */
function RedactForm({
  flag,
  pending,
  onSave,
  onCancel,
}: {
  flag: Flag;
  pending: boolean;
  onSave: (body: FlagResolve) => void;
  onCancel: () => void;
}) {
  const report = flag.report;
  const [tip, setTip] = useState(report?.tip_text ?? "");
  const [note, setNote] = useState(report?.reason_note ?? "");
  if (!report) return null;
  const tipChanged = report.tip_text !== null && tip !== report.tip_text;
  const noteChanged = report.reason_note !== null && note !== report.reason_note;
  return (
    <form
      className={styles.panel}
      aria-labelledby="redact-title"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          action: "aangepast",
          ...(tipChanged ? { redacted_tip_text: tip } : {}),
          ...(noteChanged ? { redacted_reason_note: note } : {}),
        });
      }}
    >
      <h3 id="redact-title" className={styles.panelTitle}>
        {t.redactTitle}
      </h3>
      <p className={styles.meta}>{t.redactLead}</p>
      {report.tip_text !== null && <TextArea label={t.tipText} value={tip} max={280} onChange={setTip} />}
      {report.reason_note !== null && <TextArea label={t.reasonNote} value={note} max={140} onChange={setNote} />}
      {(tipChanged || noteChanged) && (
        <div className={styles.preview} aria-live="polite">
          <div className={styles.label}>{t.preview}</div>
          <div className={styles.compare}>
            <div>
              <div className={styles.meta}>{t.before}</div>
              <p className={[styles.quote, styles.removed].join(" ")}>
                {[tipChanged && report.tip_text, noteChanged && report.reason_note].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div>
              <div className={styles.meta}>{t.after}</div>
              <p className={styles.quote}>
                {[tipChanged && (tip.trim() || "—"), noteChanged && (note.trim() || "—")].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
        </div>
      )}
      <div className={styles.actions}>
        <Button onClick={onCancel}>{t.cancel}</Button>
        <Button type="submit" variant="primary" disabled={pending || !(tipChanged || noteChanged)}>
          {t.save}
        </Button>
      </div>
    </form>
  );
}

function HideForm({
  pending,
  onHide,
  onCancel,
}: {
  pending: boolean;
  onHide: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [missing, setMissing] = useState(false);
  return (
    <form
      className={styles.panel}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!reason.trim()) {
          setMissing(true);
          return;
        }
        onHide(reason.trim());
      }}
    >
      <Field
        label={t.hideReason}
        help={t.hideReasonHelp}
        maxLength={200}
        value={reason}
        error={missing && !reason.trim() ? nl.auth.required : null}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <div className={styles.actions}>
        <Button onClick={onCancel}>{t.cancel}</Button>
        <Button type="submit" variant="primary" disabled={pending}>
          {t.hideConfirm}
        </Button>
      </div>
    </form>
  );
}

type Mode = "redact" | "hide" | "merge" | null;

export function FlagDetail({ flag }: { flag: Flag }) {
  const toast = useToast();
  const refresh = useRefreshModeration();
  const [mode, setMode] = useState<Mode>(null);

  const undo = useMutation({
    mutationFn: async ({ action, userId }: { action: FlagAction; userId: string | null }) => {
      await reopenFlag(flag.id);
      if (action === "verborgen") {
        // Keeping it makes the report visible again; reopen once more so it is back in the queue.
        await resolveFlag(flag.id, { action: "behouden" });
        await reopenFlag(flag.id);
      }
      if (action === "geblokkeerd" && userId) await setUserActive(userId, true);
    },
    onSettled: refresh,
    onError: (error) => {
      toast({ message: errorText(error) });
    },
  });

  const resolve = useMutation({
    mutationFn: (body: FlagResolve) => resolveFlag(flag.id, body),
    onSuccess: (done, body) => {
      setMode(null);
      refresh();
      const action = body.action;
      const userId = flag.user?.id ?? flag.report?.author?.id ?? null;
      const message =
        action === "samengevoegd" ? t.toasts.samengevoegd(done.company?.name ?? "") : t.toasts[action];
      // Overwritten text and a merged company cannot come back; the rest can.
      const canUndo = action === "behouden" || action === "verborgen" || action === "geblokkeerd";
      toast({
        message,
        ...(canUndo
          ? {
              onUndo: () => {
                undo.mutate({ action, userId });
              },
            }
          : {}),
      });
    },
  });

  const reopen = useMutation({
    mutationFn: () => reopenFlag(flag.id),
    onSuccess: () => {
      refresh();
      toast({ message: t.toasts.reopened });
    },
  });

  const isOpen = flag.resolved_at === null;
  const isReport = flag.target_type === "report";
  const hasAuthor = Boolean(flag.report?.author);
  const reporter = flag.reporter;

  return (
    <article className={styles.detail} aria-labelledby={`flag-${flag.id}`}>
      <header className={styles.detailHead}>
        <span className={styles.reason}>{t.reasons[flag.reason]}</span>
        <h2 id={`flag-${flag.id}`} className={styles.detailTitle}>
          {t.targets[flag.target_type]}
        </h2>
        <p className={styles.meta}>
          {reporter ? t.flaggedBy(personText(reporter)) : t.automatic} {t.flaggedOn(formatDateTime(flag.created_at))}
        </p>
      </header>
      {flag.comment && (
        <div>
          <div className={styles.label}>{reporter ? t.comment : t.commentAutomatic}</div>
          <p className={styles.quote}>{flag.comment}</p>
        </div>
      )}
      <TargetBlock flag={flag} />

      {isOpen ? (
        <section aria-labelledby={`flag-${flag.id}-actions`} className={styles.actionSection}>
          <h3 id={`flag-${flag.id}-actions`} className={styles.panelTitle}>
            {t.actionsTitle}
          </h3>
          {mode === "redact" && (
            <RedactForm
              flag={flag}
              pending={resolve.isPending}
              onSave={(body) => {
                resolve.mutate(body);
              }}
              onCancel={() => {
                setMode(null);
              }}
            />
          )}
          {mode === "hide" && (
            <HideForm
              pending={resolve.isPending}
              onHide={(reason) => {
                resolve.mutate({ action: "verborgen", hidden_reason: reason });
              }}
              onCancel={() => {
                setMode(null);
              }}
            />
          )}
          {mode !== "redact" && mode !== "hide" && (
            <div className={styles.actions}>
              <Button
                disabled={resolve.isPending}
                onClick={() => {
                  resolve.mutate({ action: "behouden" });
                }}
              >
                {t.keep}
              </Button>
              {isReport && (flag.report?.tip_text || flag.report?.reason_note) && (
                <Button
                  onClick={() => {
                    setMode("redact");
                  }}
                >
                  {t.redact}
                </Button>
              )}
              {isReport && !flag.report?.hidden_at && (
                <Button
                  onClick={() => {
                    setMode("hide");
                  }}
                >
                  {t.hide}
                </Button>
              )}
              {flag.company && (
                <Button
                  onClick={() => {
                    setMode("merge");
                  }}
                >
                  {t.merge}
                </Button>
              )}
              {((isReport && hasAuthor) || flag.target_type === "user") && (
                <Button
                  className={styles.danger}
                  disabled={resolve.isPending}
                  onClick={() => {
                    resolve.mutate({ action: "geblokkeerd" });
                  }}
                >
                  {isReport ? t.blockAuthor : t.blockUser}
                </Button>
              )}
            </div>
          )}
          <FormError message={resolve.error ? errorText(resolve.error) : null} />
        </section>
      ) : (
        <div className={styles.resolved}>
          <p className={styles.meta}>
            {t.resolvedAs(flag.action ? t.actions[flag.action] : "", formatDateTime(flag.resolved_at ?? flag.created_at))}
          </p>
          <Button
            disabled={reopen.isPending}
            onClick={() => {
              reopen.mutate();
            }}
          >
            {t.reopen}
          </Button>
          <FormError message={reopen.error ? errorText(reopen.error) : null} />
        </div>
      )}

      {flag.company && (
        <MergeDialog
          source={flag.company}
          open={mode === "merge"}
          onOpenChange={(open) => {
            setMode(open ? "merge" : null);
          }}
          pending={resolve.isPending}
          error={resolve.error}
          onConfirm={(into) => {
            resolve.mutate({ action: "samengevoegd", merge_into: into.id });
          }}
        />
      )}
    </article>
  );
}
